import { useTranslation } from 'react-i18next'
import { AlertTriangle, Loader2, Folder, FileText } from 'lucide-react'
import { FileExplorerItem } from '@features/file-explorer/types'
import Modal from './ui/Modal'

interface ConfirmDeleteModalProps {
  isOpen: boolean
  item?: FileExplorerItem | null
  items?: FileExplorerItem[]
  isDeleting: boolean
  onClose: () => void
  onConfirm: () => void
}

export default function ConfirmDeleteModal({
  isOpen,
  item,
  items,
  isDeleting,
  onClose,
  onConfirm
}: ConfirmDeleteModalProps) {
  const { t } = useTranslation()

  // Support both single item and multiple items
  const isBulk = items && items.length > 0
  const hasFolders = isBulk
    ? items.some(i => i.is_folder)
    : item?.is_folder

  if (!item && !isBulk) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={isBulk ? "md" : "sm"}
      title={t('fileExplorer.delete.title')}
      icon={<AlertTriangle className="w-6 h-6 text-error" />}
      iconClassName="bg-error/10"
      disabled={isDeleting}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost"
            disabled={isDeleting}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="btn btn-error"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <AlertTriangle className="w-4 h-4" />
            )}
            {t('fileExplorer.delete.confirm')}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {isBulk ? (
          <>
            <p className="text-base-content/80">
              {t('fileExplorer.bulkActions.confirmDelete', { count: items.length })}
            </p>

            {/* List of items to delete */}
            <div className="max-h-60 overflow-y-auto border border-base-300 rounded-lg">
              <ul className="divide-y divide-base-300">
                {items.map((itm) => (
                  <li key={`${itm.type}-${itm.id}`} className="px-3 py-2 flex items-center gap-2">
                    {itm.is_folder ? (
                      <Folder className="w-4 h-4 text-warning flex-shrink-0" />
                    ) : (
                      <FileText className="w-4 h-4 text-info flex-shrink-0" />
                    )}
                    <span className="text-sm truncate">{itm.name}</span>
                  </li>
                ))}
              </ul>
            </div>

            {hasFolders && (
              <div className="alert alert-warning">
                <AlertTriangle className="w-5 h-5" />
                <span className="text-sm">{t('fileExplorer.bulkActions.folderWarning')}</span>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="text-base-content/80">
              {t(`fileExplorer.delete.confirm${item!.is_folder ? 'Folder' : 'Document'}`, {
                name: item!.name
              })}
            </p>
            {item!.is_folder && (
              <div className="alert alert-warning">
                <AlertTriangle className="w-5 h-5" />
                <span className="text-sm">{t('fileExplorer.delete.folderWarning')}</span>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}
